// The upload workflow scales every photo down to 1024 px and rejects files above
// 20 MB or whose content does not match a JPEG, PNG or WebP. Phones and in-app
// browsers often hand over 20+ MB camera photos or HEIC/AVIF files under another
// label, so the browser redraws the photo as a moderate JPEG before uploading.
import { convertHeicToJpeg } from "./heicConversion";

export const MAX_UPLOAD_EDGE_PX = 2048;
// The second attempt in in-app browsers draws only as large as the workflow needs.
export const RETRY_UPLOAD_EDGE_PX = 1024;
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const UPLOAD_JPEG_QUALITY = 0.88;
const PREPARE_TIMEOUT_MS = 8000;

// What the upload workflow accepts, by the content of the file.
const UPLOADABLE_FORMATS = {
    jpeg: { type: "image/jpeg", extension: "jpg", knownExtension: /\.jpe?g$/i },
    png: { type: "image/png", extension: "png", knownExtension: /\.png$/i },
    webp: { type: "image/webp", extension: "webp", knownExtension: /\.webp$/i },
};
const HEIC_BRANDS = ["heic", "heix", "heim", "heis", "hevc", "hevx", "hevm", "hevs", "mif1", "msf1"];

const startsWith = (bytes, signature, offset = 0) =>
    signature.every((value, index) => bytes[offset + index] === value);
const ascii = (bytes, start, end) => String.fromCharCode(...bytes.slice(start, end));

/**
 * Names the image format by the first bytes of the file, the way the upload workflow
 * checks it. The type and name a browser reports are not reliable: phones and in-app
 * browsers hand over HEIC photos labelled as JPEG and files that are no image at all.
 * Returns "jpeg", "png", "webp", "heic", "avif", "gif" or "unknown", and null when
 * the bytes cannot be read (then nothing is known and nothing is blocked).
 */
export const detectImageFormat = async (blob) => {
    let bytes;
    try {
        bytes = new Uint8Array(await blob.slice(0, 32).arrayBuffer());
    } catch (error) {
        // sentry-ignore: an unreadable header only means the format stays unknown; the upload decides
        void error;
        return null;
    }

    if (startsWith(bytes, [0xff, 0xd8, 0xff])) return "jpeg";
    if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "png";
    if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 12) === "WEBP") return "webp";
    if (ascii(bytes, 0, 4) === "GIF8") return "gif";
    if (ascii(bytes, 4, 8) === "ftyp") {
        // Major brand at 8, compatible brands from 16; AVIF files may lead with "mif1".
        const brands = [ascii(bytes, 8, 12)];
        for (let offset = 16; offset + 4 <= bytes.length; offset += 4) brands.push(ascii(bytes, offset, offset + 4));
        if (brands.some((brand) => brand === "avif" || brand === "avis")) return "avif";
        if (brands.some((brand) => HEIC_BRANDS.includes(brand))) return "heic";
    }
    return "unknown";
};

// Instagram and Facebook open shop links in their own WebView, whose image decoding
// and canvas fail more often than in the phone's browser.
export const isInAppBrowser = (userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "") =>
    /Instagram|FBAN|FBAV|FB_IAB|FB4A|FBIOS/.test(userAgent || "");

// The <img> path can be cancelled: clearing its source stops the decode and lets
// the browser drop the pixels. `cancellation.cancel` is set while it is loading.
const loadWithImageElement = (file, cancellation) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const finish = () => {
        img.onload = null;
        // sentry-ignore: detaches the handler after the decode settled; nothing to report
        img.onerror = null;
        cancellation.cancel = null;
        URL.revokeObjectURL(url);
    };
    img.onload = () => {
        finish();
        resolve(img);
    };
    img.onerror = () => {
        // sentry-ignore: rejects; prepareImageForUpload tries the next way to prepare the photo
        finish();
        reject(new Error("image_decode_failed"));
    };
    cancellation.cancel = () => {
        finish();
        img.removeAttribute("src");
        reject(new Error("prepare_timeout"));
    };
    img.src = url;
});

const decodeImage = async (file, cancellation, imageElementOnly) => {
    if (!imageElementOnly && typeof createImageBitmap === "function") {
        try {
            return await createImageBitmap(file, { imageOrientation: "from-image" });
        } catch (error) {
            // sentry-ignore: some browsers cannot decode every format here; the <img> path below tries again
            void error;
        }
    }
    if (cancellation.cancelled) throw new Error("prepare_timeout");
    return loadWithImageElement(file, cancellation);
};

// ImageBitmap frees its pixels with close(); an <img> frees them once its source is cleared.
const releaseDecodedImage = (image) => {
    if (typeof image.close === "function") {
        image.close();
    } else if (typeof image.removeAttribute === "function") {
        image.removeAttribute("src");
    }
};

export const getUploadDimensions = (width, height, maxEdge = MAX_UPLOAD_EDGE_PX) => {
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
};

const redrawAsJpeg = async (file, cancellation, { maxEdge = MAX_UPLOAD_EDGE_PX, imageElementOnly = false } = {}) => {
    const image = await decodeImage(file, cancellation, imageElementOnly);
    let canvas;
    let width;
    let height;
    try {
        const sourceWidth = image.width || image.naturalWidth;
        const sourceHeight = image.height || image.naturalHeight;
        if (!sourceWidth || !sourceHeight) throw new Error("image_without_size");

        ({ width, height } = getUploadDimensions(sourceWidth, sourceHeight, maxEdge));
        canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("canvas_unavailable");
        // JPEG has no transparency; a white background keeps transparent PNGs readable.
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
    } finally {
        // Free the full-size pixels on every path; `released` promises exactly that.
        releaseDecodedImage(image);
    }

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", UPLOAD_JPEG_QUALITY));
    if (!blob || !blob.size) throw new Error("image_encode_failed");
    // Some WebViews answer with a PNG or with bytes that are no image; the workflow
    // would reject those as a file whose content does not match its label.
    const encodedFormat = await detectImageFormat(blob);
    if (encodedFormat && encodedFormat !== "jpeg") throw new Error("image_encode_invalid");

    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return {
        file: new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() }),
        width,
        height,
    };
};

// Resolves once an earlier preparation has let go of its photo, so two full-size
// photos are never decoded at once. A decode that hangs must not block the next
// upload for good, so the wait is capped.
export const waitForRelease = (released, maxMs = PREPARE_TIMEOUT_MS) => new Promise((resolve) => {
    const timeoutId = setTimeout(resolve, maxMs);
    const done = () => {
        clearTimeout(timeoutId);
        resolve();
    };
    Promise.resolve(released).then(done, done);
});

// One redraw with its own time limit. `released` settles once this attempt has let
// go of its full-size pixels.
const attemptRedraw = async (file, options) => {
    const cancellation = { cancelled: false, cancel: null };
    let timeoutId;
    const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
            // An <img> decode stops here; a createImageBitmap decode cannot be cancelled.
            cancellation.cancelled = true;
            cancellation.cancel?.();
            reject(new Error("prepare_timeout"));
        }, PREPARE_TIMEOUT_MS);
    });
    const redraw = redrawAsJpeg(file, cancellation, options);
    const released = redraw.then(() => undefined, () => undefined);

    try {
        return { redrawn: await Promise.race([redraw, timeout]), released };
    } catch (error) {
        // sentry-ignore: prepareImageForUpload tries the next way and reports the outcome in its result
        return { reason: error?.message || "prepare_failed", released };
    } finally {
        clearTimeout(timeoutId);
    }
};

// The workflow compares the content with the label, so a valid photo under the
// wrong type or extension gets the label that matches its content.
const withMatchingLabel = (file, format) => {
    const { type, extension, knownExtension } = UPLOADABLE_FORMATS[format];
    const name = file.name || "photo";
    if (file.type === type && knownExtension.test(name)) return file;
    return new File([file], `${name.replace(/\.[^.]+$/, "")}.${extension}`, {
        type,
        lastModified: file.lastModified,
    });
};

/**
 * Returns a JPEG of at most MAX_UPLOAD_EDGE_PX on the long edge with its size.
 *
 * A photo the browser cannot redraw gets further tries: a HEIC photo is converted
 * with heic-to and redrawn, and in-app browsers redraw once more through <img> at a
 * smaller size. If all of that fails, the original is uploaded only when its content
 * is a JPEG, PNG or WebP of at most 20 MB. Otherwise the result has
 * `uploadable: false` and `uploadValidationError` ("file_too_large" or
 * "unreadable_image", the codes the workflow uses), and the caller tells the
 * customer instead of sending a file the workflow is known to reject.
 *
 * Callers should preview the returned file, and only after `released`: decoding the
 * full-size original twice at once can exhaust memory on phones.
 */
export const prepareImageForUpload = async (file, { userAgent } = {}) => {
    if (!file || typeof document === "undefined") {
        return { file, prepared: false, uploadable: true, reason: "unsupported" };
    }

    const sourceFormat = await detectImageFormat(file);
    const inAppBrowser = isInAppBrowser(userAgent);
    const steps = [];
    const releases = [];
    // What a redraw reads: the original, or the JPEG a HEIC photo was converted to.
    let source = file;

    const redraw = async (options) => {
        const attempt = await attemptRedraw(source, options);
        releases.push(attempt.released);
        steps.push(attempt.redrawn ? "redrawn" : attempt.reason);
        return attempt;
    };

    // Browsers that decode HEIC themselves (Safari) are done here without the library.
    let attempt = await redraw();

    if (!attempt.redrawn && sourceFormat === "heic") {
        try {
            source = await convertHeicToJpeg(file);
            steps.push("heic_converted");
            attempt = await redraw();
        } catch (error) {
            // sentry-ignore: the reason is part of the result; the caller reports a photo that cannot be uploaded
            steps.push(error?.message || "heic_convert_failed");
        }
    }

    // createImageBitmap can hang or fail in these WebViews where <img> still works.
    // A HEIC photo that could not be converted would only fail the same way again.
    const retryable = sourceFormat !== "heic" || source !== file;
    if (!attempt.redrawn && inAppBrowser && retryable) {
        attempt = await redraw({ maxEdge: RETRY_UPLOAD_EDGE_PX, imageElementOnly: true });
    }

    // A hung createImageBitmap decode never settles; waitForRelease caps the wait.
    const released = Promise.all(releases).then(() => undefined);
    const diagnostics = {
        source_format: sourceFormat || "unreadable",
        in_app_browser: inAppBrowser,
        prepare_steps: steps.join(">"),
    };

    if (attempt.redrawn) {
        return { ...attempt.redrawn, prepared: true, uploadable: true, reason: "redrawn", released, diagnostics };
    }

    const reason = attempt.reason;
    const blocked = (uploadValidationError) =>
        ({ file: null, prepared: false, uploadable: false, uploadValidationError, reason, released, diagnostics });

    const uploadFormat = source === file ? sourceFormat : "jpeg";
    if (uploadFormat && !UPLOADABLE_FORMATS[uploadFormat]) return blocked("unreadable_image");
    if (source.size > MAX_UPLOAD_BYTES) return blocked("file_too_large");

    return {
        // Unreadable bytes leave the format unknown: the workflow decides as before.
        file: uploadFormat ? withMatchingLabel(source, uploadFormat) : source,
        prepared: false,
        uploadable: true,
        reason,
        released,
        diagnostics,
    };
};
